import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import api from 'services/api';
import PropTypes from 'prop-types';
import { ANAO, ASIM } from 'consts';
import MensagemDicionario from './MensagemDicionario';
import { useHistory, useParams } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';

const ROTA_MENSAGENS = '/crm/whatsapp/mensagens';

export default function MsgWhatsAppPassageiroForm({
  modal = false,
  open = true,
  registro = null,
  onClose,
  onSaved
}) {
  const history = useHistory();
  const { id } = useParams();

  const [dados, setDados] = useState({});
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [dicionarioAberto, setDicionarioAberto] = useState(false);

  const textoRef = useRef(null);

  const posicaoRef = useRef({
    inicio: 0,
    fim: 0
  });

  const titulo = `${dados.idmsgwhats ? 'Alterar' : 'Novo'} mensagem`;

  /* Carrega os dados iniciais da tela */
  useEffect(() => {
    if (!open) return;

    let active = true;

    setErro('');

    if (modal) {
      setDados(registro || {});

      return;
    }

    if (!id || id === 'novo') {
      setDados({});

      return;
    }

    api.get(ROTA_MENSAGENS).then(response => {
      if (!active) return;

      const mensagens = Array.isArray(response.data) ? response.data : [];
      const item = mensagens.find(x => String(x.idmsgwhats) === String(id));

      if (item) {
        setDados(item);
      } else {
        setErro('Registro não encontrado!');
      }
    }).catch(() => {
      if (active) setErro('Não foi possível consultar o cadastro!');
    });

    return () => {
      active = false;
    };
  }, [open, modal, registro, id]);

  /* Altera o campo da mensagem */
  const alterar = (campo, valor) => {
    setDados(d => ({
      ...d,
      [campo]: valor
    }));
  };

  /* Guarda a posição do cursor no texto da mensagem */
  const atualizarPosicao = (event) => {
    posicaoRef.current = {
      inicio: event.target.selectionStart,
      fim: event.target.selectionEnd
    };
  };

  /* Insere a tag do dicionário na posição do cursor */
  const inserirTag = (tag) => {
    const texto = dados.msg || '';
    const { inicio, fim } = posicaoRef.current;
    const pos = inicio + tag.length;

    alterar('msg', texto.slice(0, inicio) + tag + texto.slice(fim));

    posicaoRef.current = { inicio: pos, fim: pos };

    setTimeout(() => {
      if (textoRef.current) {
        textoRef.current.focus();
        textoRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  /* Evento onChange dos componentes */
  const onChangeDescricao = (event) => alterar('descricao', event.target.value);
  const onChangeInativo = (event) => alterar('inativo', event.target.checked ? ASIM : ANAO);

  const onChangeMsg = (event) => {
    atualizarPosicao(event);

    alterar('msg', event.target.value);
  };

  /* Abre e fecha o dicionário */
  const onClickDicionario = () => setDicionarioAberto(true);
  const onCloseDicionario = () => setDicionarioAberto(false);

  /* Fecha o modal ou volta para a consulta */
  const voltar = () => {
    if (modal) {
      if (onClose) onClose();
    } else {
      history.push(ROTA_MENSAGENS);
    }
  };

  /* Clicar para salvar */
  const salvar = async () => {
    if (loading) return;

    setLoading(true);
    setErro('');

    try {
      const payload = {
        descricao: (dados.descricao || '').trim(),
        msg: dados.msg || '',
        inativo: dados.inativo === ASIM ? ASIM : ANAO
      };

      if (dados.idmsgwhats) {
        await api.put(`${ROTA_MENSAGENS}/${dados.idmsgwhats}`, payload);
      } else {
        await api.post(ROTA_MENSAGENS, payload);
      }

      if (onSaved) await onSaved();

      voltar();
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível salvar o cadastro!');
    } finally {
      setLoading(false);
    }
  };

  const conteudo = (
    <>
      {erro && (
        <Typography color="error">
          {erro}
        </Typography>
      )}

      <TextField
        fullWidth
        margin="dense"
        label="Descrição"
        value={dados.descricao || ''}
        onChange={onChangeDescricao}
      />

      <Box
        mt={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="subtitle2">
          Texto da mensagem
        </Typography>

        <Button
          variant="outlined"
          color="primary"
          onClick={onClickDicionario}
        >
          Dicionário
        </Button>
      </Box>

      <TextField
        fullWidth
        multiline
        maxRowsrows={8}
        margin="dense"
        label="Texto da mensagem"
        value={dados.msg || ''}
        inputRef={textoRef}
        onClick={atualizarPosicao}
        onKeyUp={atualizarPosicao}
        onSelect={atualizarPosicao}
        onChange={onChangeMsg}
      />

      <MensagemDicionario
        open={dicionarioAberto}
        onClose={onCloseDicionario}
        onInsert={inserirTag}
      />

      <FormControlLabel
        label="Inativo"
        control={
          <Checkbox
            checked={dados.inativo === ASIM}
            onChange={onChangeInativo}
          />
        }
      />
    </>
  );

  const acoes = (
    <>
      <Button onClick={voltar}>
        Cancelar
      </Button>

      <Button
        variant="contained"
        color="primary"
        disabled={loading || !(dados.descricao || '').trim() || !(dados.msg || '').trim()}
        onClick={salvar}
      >
        {loading ? <CircularProgress size={18} /> : 'Salvar'}
      </Button>
    </>
  );

  if (modal) {
    return (
      <Dialog
        open={open}
        onClose={voltar}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {titulo}
        </DialogTitle>

        <DialogContent dividers>
          {conteudo}
        </DialogContent>

        <DialogActions>
          {acoes}
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box p={3}>
      <Paper style={{ padding: 24 }}>
        <Typography variant="h6">
          {titulo}
        </Typography>

        {conteudo}

        <Box
          mt={2}
          display="flex"
          justifyContent="flex-end"
        >
          {acoes}
        </Box>
      </Paper>
    </Box>
  );
};

MsgWhatsAppPassageiroForm.propTypes = {
  modal: PropTypes.bool,
  open: PropTypes.bool,
  registro: PropTypes.object,
  onClose: PropTypes.func,
  onSaved: PropTypes.func
};